import { Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import {
  AuthenticationCoreProvider,
  AuthRouterProvider,
  type IRouterAdapter,
} from "@ticketon/authentication-core";
import { ToastProvider } from "ui";
import { sessionBridge, sessionManager } from "@/auth/sessionBridge";
import { socialSignIn } from "@/auth/socialSignIn";
import { PermissionsProvider } from "@/auth/PermissionsProvider";
import { MarketingWorkspaceProvider } from "@/marketing/MarketingWorkspaceProvider";
import { buildLocaleHeader, toAppLocale } from "@/i18n/localeHeader";
import { router } from "./routes";
import "./i18n";
import "./index.css";

const GRAPHQL_URI = import.meta.env.VITE_GRAPHQL_ENDPOINT;

if (!GRAPHQL_URI) {
  console.error("VITE_GRAPHQL_ENDPOINT is not defined");
}

const routerAdapter: IRouterAdapter = {
  push: (path: string) => router.navigate(path),
  replace: (path: string) => router.navigate(path, { replace: true }),
  back: () => router.navigate(-1),
  getCurrentPath: () => window.location.pathname,
};

// Per-request header hook for the shared authentication GraphQL client, so auth traffic carries
// the selected language too and its errors come back localized.
function getAuthHeader(): Record<string, string> {
  const session = sessionManager.getSession();
  const localeHeader = buildLocaleHeader();

  if (session?.token) {
    return { ...localeHeader, Authorization: `Bearer ${session.token}` };
  }
  return localeHeader;
}

interface IErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, IErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg">
            <h1 className="text-xl font-bold text-red-600 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-4">{this.state.error?.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  // Follows the language the user picked, so the shared auth screens are not stuck in Spanish.
  const { i18n } = useTranslation();

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthenticationCoreProvider
          graphqlConfig={{ uri: GRAPHQL_URI || "", getAuthHeader }}
          locale={toAppLocale(i18n.resolvedLanguage || i18n.language)}
          sessionBridge={sessionBridge}
          socialSignIn={socialSignIn}
      >
          <AuthRouterProvider routerAdapter={routerAdapter}>
            <PermissionsProvider>
              <MarketingWorkspaceProvider>
                <RouterProvider router={router} />
              </MarketingWorkspaceProvider>
            </PermissionsProvider>
          </AuthRouterProvider>
        </AuthenticationCoreProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
